'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Printer, ArrowLeft, ExternalLink, Globe } from 'lucide-react';
import { stages } from '@/app/components/PropertyTaxCycleDiagram';
import PropertyTaxCycleDiagram from '@/app/components/PropertyTaxCycleDiagram';
import AppLayout from '@/components/AppLayout';

const stageColors = [
  '#f59e0b','#10b981','#3b82f6','#8b5cf6','#ec4899',
  '#f97316','#14b8a6','#06b6d4','#ef4444'
];

export default function PropertyTaxCycleClient() {
  const [printMode, setPrintMode] = useState(false);

  const handlePrint = () => {
    setPrintMode(true);
    setTimeout(() => {
      window.print();
      setPrintMode(false);
    }, 300);
  };

  return (
    <AppLayout>
      <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Link
              href="/"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-white/5"
              style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}
            >
              <ArrowLeft size={13} />
              Dashboard
            </Link>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <div
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{ backgroundColor: 'var(--accent)' }}
                />
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>
                  Methodology
                </span>
              </div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--dark-text)' }}>
                LRA Property Tax Cycle
              </h1>
              <p className="text-sm mt-0.5" style={{ color: 'var(--dark-muted)' }}>
                9-Stage Digital Intelligence System · Built on World Bank GIS Standards
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              href="/system-methodology"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-white/5"
              style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}
            >
              <Globe size={13} />
              System Methodology
            </Link>
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all hover:opacity-85 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)', color: '#070e1f' }}
            >
              <Printer size={13} />
              Print / Export
            </button>
          </div>
        </div>
        {/* Intro callout */}
        <div
          className="rounded-xl p-5 mb-6"
          style={{ background: 'linear-gradient(135deg, rgba(212,160,23,0.08) 0%, rgba(212,160,23,0.03) 100%)', border: '1px solid rgba(212,160,23,0.2)' }}
        >
          <p className="text-sm leading-relaxed" style={{ color: 'var(--dark-text)' }}>
            <span className="font-bold" style={{ color: 'var(--accent)' }}>This is why we call it a cycle.</span>{' '}
            It does not end at payment. Every year, the system becomes stronger because new data, new payments, new enforcement actions,
            and new property updates feed back into the next assessment cycle. The goal is not only to collect property tax —
            the goal is to build a <span className="font-semibold">living property tax intelligence system for Liberia.</span>
          </p>
        </div>
        {/* Main diagram */}
        <div className="mb-8">
          <PropertyTaxCycleDiagram />
        </div>
        {/* All 9 stages — detailed cards */}
        <div className="mb-8">
          <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--dark-text)' }}>
            All 9 Stages — Detailed Reference
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {stages?.map((stage) => (
              <div
                key={stage?.id}
                className="rounded-xl p-5 flex flex-col gap-3"
                style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
              >
                {/* Stage header */}
                <div className="flex items-start gap-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${stage?.color}15`, border: `1.5px solid ${stage?.color}40` }}
                  >
                    <stage.icon size={16} style={{ color: stage?.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span
                        className="text-xs font-bold px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: `${stage?.color}20`, color: stage?.color }}
                      >
                        Stage {stage?.id}
                      </span>
                    </div>
                    <h3 className="text-sm font-bold leading-tight" style={{ color: 'var(--dark-text)' }}>
                      {stage?.title}
                    </h3>
                  </div>
                </div>

                <p className="text-xs leading-relaxed" style={{ color: 'var(--dark-muted)' }}>
                  {stage?.description}
                </p>

                {/* KPIs */}
                <div className={`gap-1.5 ${stage?.kpis?.length >= 4 ? 'grid grid-cols-2' : 'grid grid-cols-3'}`}>
                  {stage?.kpis?.map((kpi) => (
                    kpi?.link ? (
                      <Link
                        key={kpi?.label}
                        href={kpi?.link}
                        className="rounded-lg p-2 text-center transition-all hover:opacity-80 hover:scale-105"
                        style={{ backgroundColor: 'var(--dark-surface)', border: `1px solid ${stage?.color}30`, cursor: 'pointer', display: 'block' }}
                      >
                        <p className="text-xs font-bold font-mono" style={{ color: stage?.color }}>{kpi?.value}</p>
                        <p className="text-xs mt-0.5 leading-tight" style={{ color: 'var(--dark-muted)', fontSize: '10px' }}>{kpi?.label}</p>
                      </Link>
                    ) : (
                      <div
                        key={kpi?.label}
                        className="rounded-lg p-2 text-center"
                        style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
                      >
                        <p className="text-xs font-bold font-mono" style={{ color: stage?.color }}>{kpi?.value}</p>
                        <p className="text-xs mt-0.5 leading-tight" style={{ color: 'var(--dark-muted)', fontSize: '10px' }}>{kpi?.label}</p>
                      </div>
                    )
                  ))}
                </div>

                {/* Module link */}
                <Link
                  href={stage?.moduleLink}
                  className="flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold transition-all hover:opacity-85 mt-auto"
                  style={{ backgroundColor: `${stage?.color}10`, border: `1px solid ${stage?.color}25`, color: stage?.color }}
                >
                  <span>{stage?.moduleName}</span>
                  <ExternalLink size={11} />
                </Link>
              </div>
            ))}
          </div>
        </div>
        {/* Cycle summary stats */}
        <div
          className="rounded-xl p-5 mb-6"
          style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
        >
          <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--dark-text)' }}>
            Cycle Performance Summary — FY 2026
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {[
              { label: 'Properties in Cycle', value: '4,818', color: '#f59e0b' },
              { label: 'Bills Generated', value: '4,818', color: '#ec4899' },
              { label: 'Payments Collected', value: '$3,208,788', color: '#14b8a6' },
              { label: 'Compliance Rate', value: '44.4%', color: '#06b6d4' },
              { label: 'Enforcement Cases', value: '2,679', color: '#ef4444' },
            ]?.map((stat) => (
              <div
                key={stat?.label}
                className="rounded-lg p-3 text-center"
                style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
              >
                <p className="text-xl font-bold font-mono" style={{ color: stat?.color }}>{stat?.value}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--dark-muted)' }}>{stat?.label}</p>
              </div>
            ))}
          </div>
        </div>
        {/* Print styles */}
        <style>{`
          @media print {
            body { background: white !important; color: black !important; }
            .no-print { display: none !important; }
          }
        `}</style>
      </div>
    </AppLayout>
  );
}
